import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

/**
 * CountdownTimer - Displays remaining time until quote expiration
 *
 * @param {string} expirationDate - ISO date string for when quote expires
 * @param {function} onExpire - Callback function when timer reaches 0
 * @param {string} variant - 'mobile' or 'desktop' for different styling
 */
const CountdownTimer = ({ expirationDate, onExpire, variant = 'mobile' }) => {
  const { t } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!expirationDate) return;

    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const expiration = new Date(expirationDate).getTime();
      const difference = expiration - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeRemaining({ minutes: 0, seconds: 0 });
        if (onExpire) {
          onExpire();
        }
        return null;
      }

      const minutes = Math.floor(difference / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { minutes, seconds };
    };

    // Initial calculation
    const initialTime = calculateTimeRemaining();
    if (initialTime) {
      setTimeRemaining(initialTime);
    }

    // Update every second
    const interval = setInterval(() => {
      const time = calculateTimeRemaining();
      if (time) {
        setTimeRemaining(time);
      } else {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expirationDate, onExpire]);

  // Don't render if no expiration date
  if (!expirationDate || !timeRemaining) {
    return null;
  }

  // Format time as MM:SS
  const formatTime = () => {
    const mins = String(timeRemaining.minutes).padStart(2, '0');
    const secs = String(timeRemaining.seconds).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  // Determine if time is running low (less than 5 minutes)
  const isWarning = timeRemaining.minutes < 5 && !isExpired;
  const isCritical = timeRemaining.minutes < 2 && !isExpired;

  const getClassName = () => {
    let className = `countdown-timer countdown-timer--${variant}`;
    if (isExpired) {
      className += ' countdown-timer--expired';
    } else if (isCritical) {
      className += ' countdown-timer--critical';
    } else if (isWarning) {
      className += ' countdown-timer--warning';
    }
    return className;
  };

  return (
    <div className={getClassName()} role="timer" aria-live="polite">
      <AccessTimeIcon className="countdown-timer__icon" />
      {isExpired ? (
        <span className="countdown-timer__text">{t('reservation.wizard.countdown.expired')}</span>
      ) : (
        <span className="countdown-timer__text">
          {t('reservation.wizard.countdown.timeRemaining', { time: formatTime() })}
        </span>
      )}
    </div>
  );
};

export default CountdownTimer;
